/**
 * WS3's upload pickup: what the server changed under a live workspace (a user's upload, announced
 * by `workspace.changed`) is written into its executor, so the next call sees it. The frame also
 * follows a synced batch, the holder's own echo: its listing finds nothing to pull.
 *
 * Never over a change of the host's own: a live file that differs from what the server held
 * before (the sync engine's `serverHolds`) has a change not on the server yet, and it is kept; the
 * sync engine sends it in its turn, and the server's copy gives way to it. A file the host never
 * read or wrote (still lazy) is as the server sent it, so it is not read to be compared. A file
 * taken is recorded as the server's, so the sync engine does not send it back.
 */
import { sha256Hex } from "../core/bytes";
import { SandboxError } from "../core/errors";
import { WORKSPACE } from "../core/paths";
import type { Executor } from "../core/types";
import type { WorkspaceFile } from "../client/api";
import type { SyncEngine } from "../sync/engine";

/** The Files panel REST (C9): its listing and one file's bytes. */
export interface PanelFiles {
  list(workspaceId: string): Promise<WorkspaceFile[]>;
  read(workspaceId: string, path: string): Promise<Uint8Array>;
}

/** The live file: absent, a folder, still lazy (as the server sent it), or its bytes' sha256. */
type Local =
  | { kind: "absent" }
  | { kind: "folder" }
  | { kind: "lazy" }
  | { kind: "file"; sha256: string };

async function localFile(executor: Executor, path: string): Promise<Local> {
  try {
    const info = await executor.stat(path);
    if (info.type === "directory") return { kind: "folder" };
    if (!info.loaded) return { kind: "lazy" };
    return { kind: "file", sha256: await sha256Hex(await executor.exportFile(path)) };
  } catch (error) {
    if (error instanceof SandboxError && error.code === "ENOENT") return { kind: "absent" };
    throw error;
  }
}

/**
 * The host changed the file and the server does not hold that yet: a write, or a delete of what
 * the server held (or may hold: a change the engine is unsure of). Without a sync engine nothing
 * says what the server held, so any file the host has counts as its own.
 */
function ownChange(local: Local, before: string | undefined): boolean {
  if (local.kind === "lazy") return false;
  if (local.kind === "absent") return before !== undefined;
  return local.kind === "folder" || local.sha256 !== before;
}

export interface Pickup {
  /** The paths written. */
  taken: string[];
  /** Files that could not be read or written: a later pickup tries them again. */
  failed: number;
}

/**
 * Pulls the panel's files the server holds anew into the executor; `current` turning false (the
 * hold ended) stops it. A file that fails is logged and the rest still go; the listing failing
 * rejects.
 */
export async function pickUpFiles(
  workspaceId: string,
  executor: Executor,
  sync: SyncEngine | null,
  files: PanelFiles,
  current: () => boolean,
  log: Pick<Console, "warn"> = console
): Promise<Pickup> {
  const result: Pickup = { taken: [], failed: 0 };
  for (const file of await files.list(workspaceId)) {
    if (!current()) break;
    if (!file.sha256 || file.kept === false) continue;
    const before = sync?.serverHolds(file.path);
    if (before === file.sha256) continue; // nothing new
    const path = `${WORKSPACE}/${file.path}`;
    try {
      const local = await localFile(executor, path);
      if (local.kind === "file" && local.sha256 === file.sha256) {
        sync?.recordHolds(file.path, file.sha256);
        continue;
      }
      if (ownChange(local, before)) continue;
      const bytes = await files.read(workspaceId, file.path);
      // Changed again since the listing: its own frame brings the newer one.
      if (!current() || (await sha256Hex(bytes)) !== file.sha256) continue;
      // Recorded first: the write marks the path dirty, and the engine then finds it on the server.
      sync?.recordHolds(file.path, file.sha256);
      try {
        await executor.write(path, bytes);
      } catch (error) {
        // Not written: what the engine knew stands, or the absent file would read as a delete.
        if (sync?.serverHolds(file.path) === file.sha256) sync.recordHolds(file.path, before);
        throw error;
      }
      result.taken.push(path);
    } catch (error) {
      result.failed++;
      log.warn(`sandbox host: ${file.path} was not picked up`, error);
    }
  }
  return result;
}
